<app-page-shell>
  <app-page-hero
    eyebrow="Transport Planning"
    title="Order Releases"
    subtitle="Work that needs a truck. Nothing here creates a shipment until a plan is approved.">
    <div actions>
      <p-button label="New Release" icon="pi pi-plus" (onClick)="createRelease()"></p-button>
      <p-button label="Bulk Upload" icon="pi pi-upload" severity="secondary" [outlined]="true" routerLink="/transport-planning/releases/bulk-upload"></p-button>
      <p-button label="Orchestrations" icon="fa-solid fa-network-wired" severity="secondary" [outlined]="true" routerLink="/transport-planning/orchestrations"></p-button>
      <p-button
        [label]="truckPlanningActionLabel()"
        icon="fa-solid fa-truck"
        severity="secondary"
        [outlined]="true"
        [disabled]="selectAllMatching || selected.size === 0"
        pTooltip="Building a truck plan requires explicitly selected releases."
        (onClick)="buildTruckPlan()"></p-button>
      <p-button
        [label]="addToShipmentActionLabel()"
        icon="fa-solid fa-boxes-packing"
        severity="secondary"
        [outlined]="true"
        [disabled]="selectAllMatching || selected.size === 0"
        pTooltip="Add selected order releases to existing shipments."
        (onClick)="addToShipment()"></p-button>
      <p-button [label]="planningActionLabel()" icon="fa-solid fa-route" severity="secondary" [outlined]="true" (onClick)="planLoads()"></p-button>
    </div>
  </app-page-hero>

  <app-page-data-panel title="Release Pool" subtitle="Choose ready releases from this list when building a truck plan." [resultText]="releasePoolResultText()">
    @if (message) {
      <div class="notice">{{ message }}</div>
    }
    <section class="release-filter-panel" aria-label="Order release filters">
      <div class="filter-panel-head">
        <div>
          <strong>Filters</strong>
          <small>
            Search release headers and item-line details.
            @if (activeFilterCount() > 0) {
              <span class="active-filter-count">{{ activeFilterCount() }} active</span>
            }
          </small>
        </div>
        <div class="filter-actions">
          <p-button
            [label]="filtersExpanded ? 'Hide Filters' : 'Show Filters'"
            [icon]="filtersExpanded ? 'pi pi-chevron-up' : 'pi pi-chevron-down'"
            severity="secondary"
            [outlined]="true"
            size="small"
            (onClick)="toggleFilters()"></p-button>
          <p-button
            label="Clear"
            icon="pi pi-filter-slash"
            severity="secondary"
            [outlined]="true"
            size="small"
            [disabled]="activeFilterCount() === 0"
            (onClick)="resetFilters()"></p-button>
        </div>
      </div>

      @if (activeFilterChips(); as chips) {
        @if (chips.length) {
          <div class="active-filter-summary" aria-label="Active filters">
            <strong>Active filters</strong>
            <div class="active-filter-chips">
              @for (chip of chips; track chip.key + ':' + chip.value) {
                <button type="button" class="active-filter-chip" [attr.aria-label]="'Remove ' + chip.label + ' filter'" (click)="removeFilterChip(chip)">
                  <span>{{ chip.label }}</span><i class="pi pi-times" aria-hidden="true"></i>
                </button>
              }
            </div>
          </div>
        }
      }

      @if (filtersExpanded) {
        <div class="filter-group-title">Header</div>
        <div class="filter-grid header-filter-grid">
          <p-inputgroup>
            <p-inputgroup-addon><i class="pi pi-hashtag"></i></p-inputgroup-addon>
            <p-iftalabel>
              <input pInputText id="release-filter" [(ngModel)]="filters.release" (ngModelChange)="onFiltersChanged()">
              <label for="release-filter">Release / tracking</label>
            </p-iftalabel>
          </p-inputgroup>
          <p-inputgroup>
            <p-inputgroup-addon><i class="pi pi-bookmark"></i></p-inputgroup-addon>
            <p-iftalabel>
              <input pInputText id="source-number-filter" [(ngModel)]="filters.sourceNumber" (ngModelChange)="onFiltersChanged()">
              <label for="source-number-filter">Source number</label>
            </p-iftalabel>
          </p-inputgroup>
          <p-inputgroup>
            <p-inputgroup-addon><i class="fa-solid fa-diagram-project"></i></p-inputgroup-addon>
            <p-iftalabel>
              <p-multiSelect inputId="logistics-project-filter" [options]="projectOptions()" [(ngModel)]="filters.logisticsProjectIds" (ngModelChange)="onFiltersChanged()" (onPanelShow)="onProjectPanelShow()" (onPanelHide)="onProjectPanelHide()" optionLabel="label" optionValue="value" [filter]="true" filterBy="label" [showClear]="true" [maxSelectedLabels]="0" selectedItemsLabel="{0} projects selected" defaultLabel="Select projects" appendTo="body"></p-multiSelect>
              <label for="logistics-project-filter">Logistics Project</label>
            </p-iftalabel>
          </p-inputgroup>
          <p-inputgroup>
            <p-inputgroup-addon><i class="pi pi-building"></i></p-inputgroup-addon>
            <p-iftalabel>
              <p-multiSelect inputId="crm-client-filter" [options]="crmClientOptions()" [(ngModel)]="filters.crmClientIds" (ngModelChange)="onFiltersChanged()" optionLabel="label" optionValue="value" [filter]="true" filterBy="label" [showClear]="true" defaultLabel="Select clients" appendTo="body"></p-multiSelect>
              <label for="crm-client-filter">CRM Client</label>
            </p-iftalabel>
          </p-inputgroup>
          <p-inputgroup>
            <p-inputgroup-addon><i class="pi pi-globe"></i></p-inputgroup-addon>
            <p-iftalabel>
              <p-multiSelect inputId="pickup-country-filter" [options]="pickupCountryOptions()" [(ngModel)]="filters.pickupCountryIds" (ngModelChange)="onPickupCountryChanged()" optionLabel="label" optionValue="value" [filter]="true" filterBy="label" [showClear]="true" defaultLabel="Select countries" appendTo="body"></p-multiSelect>
              <label for="pickup-country-filter">Pickup Country</label>
            </p-iftalabel>
          </p-inputgroup>
            <p-inputgroup>
              <p-inputgroup-addon><i class="pi pi-map"></i></p-inputgroup-addon>
              <p-iftalabel>
              <p-multiSelect inputId="pickup-city-filter" [options]="pickupCityOptions()" [(ngModel)]="filters.pickupCityIds" (ngModelChange)="onFiltersChanged()" optionLabel="label" optionValue="value" [filter]="true" filterBy="label" [showClear]="true" defaultLabel="Select cities" appendTo="body"></p-multiSelect>
              <label for="pickup-city-filter">Pickup City</label>
            </p-iftalabel>
          </p-inputgroup>
          <p-inputgroup>
            <p-inputgroup-addon><i class="pi pi-flag"></i></p-inputgroup-addon>
            <p-iftalabel>
              <p-multiSelect inputId="dropoff-country-filter" [options]="dropoffCountryOptions()" [(ngModel)]="filters.dropoffCountryIds" (ngModelChange)="onDropoffCountryChanged()" optionLabel="label" optionValue="value" [filter]="true" filterBy="label" [showClear]="true" defaultLabel="Select countries" appendTo="body"></p-multiSelect>
              <label for="dropoff-country-filter">Dropoff Country</label>
            </p-iftalabel>
          </p-inputgroup>
            <p-inputgroup>
              <p-inputgroup-addon><i class="pi pi-map"></i></p-inputgroup-addon>
              <p-iftalabel>
              <p-multiSelect inputId="dropoff-city-filter" [options]="dropoffCityOptions()" [(ngModel)]="filters.dropoffCityIds" (ngModelChange)="onFiltersChanged()" optionLabel="label" optionValue="value" [filter]="true" filterBy="label" [showClear]="true" defaultLabel="Select cities" appendTo="body"></p-multiSelect>
              <label for="dropoff-city-filter">Dropoff City</label>
            </p-iftalabel>
          </p-inputgroup>
          <p-inputgroup>
            <p-inputgroup-addon><i class="pi pi-map-marker"></i></p-inputgroup-addon>
            <p-iftalabel>
              <p-multiSelect inputId="pickup-zone-filter" [options]="pickupZoneOptions()" [(ngModel)]="filters.pickupZoneIds" (ngModelChange)="onPickupZoneChanged()" optionLabel="label" optionValue="value" [filter]="true" filterBy="label" [showClear]="true" defaultLabel="Select zones" appendTo="body"></p-multiSelect>
              <label for="pickup-zone-filter">Pickup Zone</label>
            </p-iftalabel>
          </p-inputgroup>
          <p-inputgroup>
            <p-inputgroup-addon><p-checkbox inputId="pickup-neighbours-filter" [(ngModel)]="filters.includePickupNeighbours" (ngModelChange)="onFiltersChanged()" [binary]="true" [disabled]="!filters.pickupZoneIds.length"></p-checkbox></p-inputgroup-addon>
            <p-iftalabel>
              <input pInputText id="pickup-neighbours-label" value="Include direct neighbours" readonly>
              <label for="pickup-neighbours-label">Pickup Coverage</label>
            </p-iftalabel>
          </p-inputgroup>
          <p-inputgroup>
            <p-inputgroup-addon><i class="pi pi-map-marker"></i></p-inputgroup-addon>
            <p-iftalabel>
              <p-multiSelect inputId="dropoff-zone-filter" [options]="dropoffZoneOptions()" [(ngModel)]="filters.dropoffZoneIds" (ngModelChange)="onDropoffZoneChanged()" optionLabel="label" optionValue="value" [filter]="true" filterBy="label" [showClear]="true" defaultLabel="Select zones" appendTo="body"></p-multiSelect>
              <label for="dropoff-zone-filter">Dropoff Zone</label>
            </p-iftalabel>
          </p-inputgroup>
          <p-inputgroup>
            <p-inputgroup-addon><p-checkbox inputId="dropoff-neighbours-filter" [(ngModel)]="filters.includeDropoffNeighbours" (ngModelChange)="onFiltersChanged()" [binary]="true" [disabled]="!filters.dropoffZoneIds.length"></p-checkbox></p-inputgroup-addon>
            <p-iftalabel>
              <input pInputText id="dropoff-neighbours-label" value="Include direct neighbours" readonly>
              <label for="dropoff-neighbours-label">Dropoff Coverage</label>
            </p-iftalabel>
          </p-inputgroup>
          <p-inputgroup>
            <p-inputgroup-addon><i class="pi pi-map-marker"></i></p-inputgroup-addon>
            <p-iftalabel>
              <p-multiSelect inputId="origin-location-filter" [options]="originLocationOptions()" [(ngModel)]="filters.originLocationIds" (ngModelChange)="onFiltersChanged()" optionLabel="label" optionValue="value" [filter]="true" filterBy="label" [showClear]="true" defaultLabel="Select locations" appendTo="body"></p-multiSelect>
              <label for="origin-location-filter">From Location</label>
            </p-iftalabel>
          </p-inputgroup>
          <p-inputgroup>
            <p-inputgroup-addon><i class="pi pi-flag"></i></p-inputgroup-addon>
            <p-iftalabel>
              <p-multiSelect inputId="destination-location-filter" [options]="destinationLocationOptions()" [(ngModel)]="filters.destinationLocationIds" (ngModelChange)="onFiltersChanged()" optionLabel="label" optionValue="value" [filter]="true" filterBy="label" [showClear]="true" defaultLabel="Select locations" appendTo="body"></p-multiSelect>
              <label for="destination-location-filter">To Location</label>
            </p-iftalabel>
          </p-inputgroup>
          <p-inputgroup>
            <p-inputgroup-addon><i class="pi pi-calendar-plus"></i></p-inputgroup-addon>
            <p-iftalabel>
              <p-datepicker inputId="need-from-filter" [(ngModel)]="filters.needFrom" (ngModelChange)="onFiltersChanged()" [showIcon]="true" [showButtonBar]="true" appendTo="body"></p-datepicker>
              <label for="need-from-filter">Need from</label>
            </p-iftalabel>
          </p-inputgroup>
          <p-inputgroup>
            <p-inputgroup-addon><i class="pi pi-calendar-times"></i></p-inputgroup-addon>
            <p-iftalabel>
              <p-datepicker inputId="need-to-filter" [(ngModel)]="filters.needTo" (ngModelChange)="onFiltersChanged()" [showIcon]="true" [showButtonBar]="true" appendTo="body"></p-datepicker>
              <label for="need-to-filter">Need to</label>
            </p-iftalabel>
          </p-inputgroup>
          <p-inputgroup>
            <p-inputgroup-addon><i class="pi pi-check-circle"></i></p-inputgroup-addon>
            <p-iftalabel>
              <p-multiSelect
                inputId="status-filter"
                [options]="statusOptions()"
                [(ngModel)]="filters.status"
                (ngModelChange)="onFiltersChanged()"
                optionLabel="label"
                optionValue="value"
                [showClear]="true"
                defaultLabel="Select statuses"
                display="chip"
                appendTo="body"></p-multiSelect>
              <label for="status-filter">Status</label>
            </p-iftalabel>
          </p-inputgroup>
        </div>

        <div class="filter-group-title">Item Lines</div>
        <div class="filter-grid item-filter-grid">
          <p-inputgroup>
            <p-inputgroup-addon><i class="fa-solid fa-map-location-dot"></i></p-inputgroup-addon>
            <p-iftalabel>
              <p-multiSelect inputId="region-filter" [options]="regionOptions()" [(ngModel)]="filters.regionIds" (ngModelChange)="onRegionChanged()" optionLabel="label" optionValue="value" [filter]="true" filterBy="label" [showClear]="true" defaultLabel="Select regions" appendTo="body"></p-multiSelect>
              <label for="region-filter">Source Region</label>
            </p-iftalabel>
          </p-inputgroup>
          <p-inputgroup>
            <p-inputgroup-addon><i class="fa-solid fa-warehouse"></i></p-inputgroup-addon>
            <p-iftalabel>
              <p-multiSelect inputId="warehouse-filter" [options]="warehouseOptions()" [(ngModel)]="filters.warehouseIds" (ngModelChange)="onFiltersChanged()" optionLabel="label" optionValue="value" [filter]="true" filterBy="label" [showClear]="true" defaultLabel="Select warehouses" appendTo="body"></p-multiSelect>
              <label for="warehouse-filter">Source Warehouse</label>
            </p-iftalabel>
          </p-inputgroup>
          <p-inputgroup class="warehouse-item-filter">
            <p-inputgroup-addon><i class="pi pi-barcode"></i></p-inputgroup-addon>
            <p-iftalabel>
              <p-multiSelect
                inputId="warehouse-item-filter"
                [options]="warehouseItemOptions()"
                [(ngModel)]="filters.inventoryItemIds"
                (ngModelChange)="onFiltersChanged()"
                optionValue="value"
                [filter]="true"
                optionLabel="label"
                filterBy="label,metadata"
                [showClear]="true"
                defaultLabel="Select items"
                appendTo="body"
                styleClass="warehouse-item-select"
                panelStyleClass="warehouse-item-panel">
                <ng-template #item let-item>
                  <div class="warehouse-item-option">
                    <strong>{{ item.label }}</strong>
                    @if (item.metadata) {
                      <small>{{ item.metadata }}</small>
                    }
                  </div>
                </ng-template>
              </p-multiSelect>
              <label for="warehouse-item-filter">Warehouse Item / SKU</label>
            </p-iftalabel>
          </p-inputgroup>
          <p-inputgroup>
            <p-inputgroup-addon><i class="pi pi-sort-numeric-down"></i></p-inputgroup-addon>
            <p-iftalabel>
              <input pInputText id="quantity-min-filter" type="number" min="0" [(ngModel)]="filters.quantityMin" (ngModelChange)="onFiltersChanged()">
              <label for="quantity-min-filter">Quantity min</label>
            </p-iftalabel>
          </p-inputgroup>
          <p-inputgroup>
            <p-inputgroup-addon><i class="pi pi-sort-numeric-up"></i></p-inputgroup-addon>
            <p-iftalabel>
              <input pInputText id="quantity-max-filter" type="number" min="0" [(ngModel)]="filters.quantityMax" (ngModelChange)="onFiltersChanged()">
              <label for="quantity-max-filter">Quantity max</label>
            </p-iftalabel>
          </p-inputgroup>
          <p-inputgroup>
            <p-inputgroup-addon><i class="pi pi-calculator"></i></p-inputgroup-addon>
            <p-iftalabel>
              <p-multiSelect inputId="uom-filter" [options]="uomOptions()" [(ngModel)]="filters.uoms" (ngModelChange)="onFiltersChanged()" optionLabel="label" optionValue="value" [showClear]="true" defaultLabel="Select UOMs" appendTo="body"></p-multiSelect>
              <label for="uom-filter">UOM</label>
            </p-iftalabel>
          </p-inputgroup>
        </div>
      }
    </section>

    <div class="release-card-toolbar">
      <div class="release-toolbar-left">
        <p-selectButton
          [options]="viewModeOptions"
          [(ngModel)]="viewMode"
          (ngModelChange)="onViewModeChanged()"
          optionLabel="label"
          optionValue="value"
          [allowEmpty]="false"></p-selectButton>
        <label class="select-visible">
          <p-checkbox
            [binary]="true"
            [ngModel]="allPlanableVisibleSelected()"
            (ngModelChange)="toggleVisibleSelection($event)"
            [ngModelOptions]="{ standalone: true }"
            [disabled]="!planableVisibleReleases().length"></p-checkbox>
          <span>Select visible releases</span>
        </label>
        <label class="select-visible select-visible--ready">
          <p-checkbox
            [binary]="true"
            [ngModel]="allReadyVisibleSelected()"
            (ngModelChange)="toggleReadyVisibleSelection($event)"
            [ngModelOptions]="{ standalone: true }"
            [disabled]="!readyVisibleReleases().length"></p-checkbox>
          <span>Select ready releases</span>
        </label>
        <label class="select-visible select-visible--matching">
          <p-checkbox
            [binary]="true"
            [(ngModel)]="selectAllMatching"
            (ngModelChange)="toggleAllMatching($event)"
            [ngModelOptions]="{ standalone: true }"
            [disabled]="!totalRecords"></p-checkbox>
          <span>Select all {{ totalRecords }} matching</span>
        </label>
        <small class="muted-line">Select rescheduled cargo individually to prepare its replacement.</small>
      </div>
      <span class="muted-line">
        {{ planningSelectionCount() }} selected |
        {{ readyVisibleReleases().length }} ready on this page |
        {{ planableVisibleReleases().length }} planable on this page |
        {{ displayedEntryCountLabel() }}
      </span>
    </div>

    @if (paginationTotalRecords() > tableRows) {
      <div class="release-pagination release-pagination--top">
        <p-paginator
          [rows]="tableRows"
          [first]="first"
          [totalRecords]="paginationTotalRecords()"
          [rowsPerPageOptions]="rowsPerPageOptions"
          [showFirstLastIcon]="true"
          [showCurrentPageReport]="true"
          [currentPageReportTemplate]="'Showing {first}-{last} of {totalRecords} ' + paginationEntryLabel()"
          (onPageChange)="onPageChange($event)"></p-paginator>
      </div>
    }

    <ng-template #releaseDetails let-release>
      <div class="parent-grid">
        <div class="detail-card">
          <span>Customer</span>
          <strong>{{ customerLabel(release) }}</strong>
          <small>{{ accountLabel(release) || customerMeta(release) || 'No CRM client or legacy customer linked' }}</small>
        </div>
        <div class="detail-card">
          <span>Route</span>
          <strong>{{ collapsedPickupLabel(release) }}</strong>
          <small>{{ collapsedPickupMeta(release) }} → {{ collapsedDropoffLabel(release) }} · {{ collapsedDropoffMeta(release) }}</small>
        </div>
        <div class="detail-card">
          <span>Load</span>
          <strong>{{ loadSummary(release) }}</strong>
          <small>{{ releaseLines(release).length }} item line{{ releaseLines(release).length === 1 ? '' : 's' }}</small>
        </div>
        <div class="detail-card">
          <span>Planning</span>
          <strong>{{ linkedShipmentLabel(release) }}</strong>
          <small>{{ assignmentSummary(release) || 'Assignment pending' }}</small>
        </div>
        <div class="detail-card">
          <span>Window</span>
          <strong>{{ releaseWindowLabel(release) }}</strong>
          <small>{{ deliveryWindowLabel(release) }}</small>
        </div>
        <div class="detail-card">
          <span>Execution Note</span>
          <strong>{{ releaseExecutionNote(release) }}</strong>
          <small>{{ routeSummary(release) }}</small>
        </div>
      </div>
      @if (releaseLines(release).length) {
        <div class="release-items-detail">
          <strong>Item lines</strong>
          <div class="line-detail-list">
            @for (line of releaseLines(release); track line.releaseLineId || line.sourceLineId || $index) {
              <div class="line-detail-row">
                <strong>{{ itemName(line) }}</strong>
                <small>{{ line.itemNumber || line.itemCode || 'Item number not set' }} @if (line.skuNumber || line.inventoryStockUnitCode) { · {{ lineSkuLabel(line) }} }</small>
                <small>{{ lineQuantity(line) }} · {{ lineMeta(line) }}</small>
              </div>
            }
          </div>
        </div>
      }
    </ng-template>

    @if (isLoading && !groupedReleases.length) {
      <div class="empty-card">Loading order releases...</div>
    } @else if (!groupedReleases.length) {
      <div class="empty-card">No order releases match the current filters.</div>
    } @else if (viewMode === 'FLAT') {
      <p-table [value]="groupedReleases" dataKey="key" [rowHover]="true"
          styleClass="p-datatable-sm release-directory-table" [tableStyle]="{ 'min-width': '88rem' }">
          <ng-template pTemplate="header">
            <tr>
              <th class="directory-controls">Select / expand</th>
              <th>Release &amp; project</th>
              <th>Pickup</th>
              <th>Dropoff</th>
              <th>Item / load</th>
              <th>Need by &amp; status</th>
            </tr>
          </ng-template>
          <ng-template pTemplate="body" let-group>
            @let release = groupRelease(group);
            <tr>
              <td>
                <div class="directory-row-controls">
                  <p-checkbox [binary]="true" [ariaLabel]="'Select ' + (release.releaseNumber || 'release')"
                    [disabled]="!isCurrentFilterMatch(release) || !isPlanable(release) || (selectAllMatching && isRescheduled(release))"
                    [ngModel]="isSelected(release)" (ngModelChange)="toggleSelection(release, $event)"
                    [ngModelOptions]="{ standalone: true }"></p-checkbox>
                  <button type="button" class="directory-expand" [attr.aria-expanded]="isGroupExpanded(group.key)"
                    [attr.aria-label]="(isGroupExpanded(group.key) ? 'Collapse ' : 'Expand ') + (release.releaseNumber || 'release')"
                    (click)="toggleGroup(group.key)">
                    <i class="pi" [ngClass]="isGroupExpanded(group.key) ? 'pi-chevron-down' : 'pi-chevron-right'" aria-hidden="true"></i>
                  </button>
                </div>
              </td>
              <td>
                <div class="directory-cell">
                  <a class="release-link" [routerLink]="['/transport-planning/releases', release.releaseId]">{{ release.releaseNumber || ('Release #' + (release.releaseId || '-')) }}</a>
                  <p-tag [value]="roleLabel(release)" [severity]="roleSeverity(release)"></p-tag>
                  @if (canCancelRelease(release)) {
                    <p-button label="Cancel" icon="pi pi-ban" severity="danger" [text]="true" size="small" (onClick)="openCancelDialog(release)"></p-button>
                  }
                  @if (logisticsProjectLabel(release)) { <small>Project {{ logisticsProjectLabel(release) }}</small> }
                  <small>{{ customerLabel(release) }}</small>
                </div>
              </td>
              <td><div class="directory-cell"><strong>{{ collapsedPickupLabel(release) }}</strong><small>{{ collapsedPickupMeta(release) }}</small></div></td>
              <td><div class="directory-cell"><strong>{{ collapsedDropoffLabel(release) }}</strong><small>{{ collapsedDropoffMeta(release) }}</small></div></td>
              <td>
                @if (releaseLines(release)[0]; as line) {
                  <div class="directory-cell">
                    <strong>{{ itemName(line) }}</strong>
                    <small>{{ line.itemNumber || line.itemCode || 'Item number not set' }} @if (line.skuNumber || line.inventoryStockUnitCode) { · {{ lineSkuLabel(line) }} }</small>
                    <span>{{ lineQuantity(line) }}</span>
                    <small>{{ itemSizeLabel(line) }}</small>
                    @if (itemVolumeLabel(line)) { <small>{{ itemVolumeLabel(line) }}</small> }
                    @if (releaseLines(release).length > 1) { <span class="more-lines">+{{ releaseLines(release).length - 1 }} more item lines</span> }
                  </div>
                } @else { <span class="directory-missing">No item lines</span> }
              </td>
              <td>
                <div class="directory-cell">
                  <strong>{{ needByLabel(release) }}</strong>
                  <p-tag [value]="planningStateLabel(release)" [severity]="planningSeverity(release)"></p-tag>
                  <small>{{ groupProgressLabel(group) }}</small>
                </div>
              </td>
            </tr>
            @if (isGroupExpanded(group.key)) {
              <tr class="directory-detail-row"><td colspan="6"><ng-container *ngTemplateOutlet="releaseDetails; context: { $implicit: release }"></ng-container></td></tr>
            }
          </ng-template>
      </p-table>
    } @else {
      <div class="release-groups">
        @for (group of groupedReleases; track group.key) {
          <section class="release-group">
            <div class="group-toggle" role="button" tabindex="0" (click)="toggleGroup(group.key)" (keydown.enter)="toggleGroup(group.key)" (keydown.space)="toggleGroup(group.key)">
              <div class="group-toggle-main">
                <div class="group-chevron">
                  <i class="pi" [ngClass]="isGroupExpanded(group.key) ? 'pi-chevron-down' : 'pi-chevron-right'"></i>
                </div>
                <label class="group-select" (click)="$event.stopPropagation()">
                  <p-checkbox
                    [binary]="true"
                    [disabled]="!isPlanable(groupRelease(group)) || (selectAllMatching && isRescheduled(groupRelease(group)))"
                    [ngModel]="isSelected(groupRelease(group))"
                    (ngModelChange)="toggleSelection(groupRelease(group), $event)"
                    [ngModelOptions]="{ standalone: true }"></p-checkbox>
                </label>
                <div class="group-heading">
                  <div class="group-kicker">{{ groupTitle(group) }}</div>
                  <div class="group-title-row">
                    @if (group.parent?.releaseId) {
                      <a class="release-link" [routerLink]="['/transport-planning/releases', group.parent?.releaseId]" (click)="$event.stopPropagation()">
                        {{ group.parent?.releaseNumber || ('Release #' + (group.parent?.releaseId || '-')) }}
                      </a>
                    } @else if (group.kind === 'INTEGRITY_ERROR') {
                      <span class="release-link missing-parent">Parent #{{ group.parentReleaseId || '-' }}</span>
                    } @else {
                      <a class="release-link" [routerLink]="['/transport-planning/releases', group.anchor.releaseId]" (click)="$event.stopPropagation()">
                        {{ group.anchor.releaseNumber || ('Release #' + (group.anchor.releaseId || '-')) }}
                      </a>
                    }
                    <p-tag [value]="roleLabel(groupRelease(group))" [severity]="roleSeverity(groupRelease(group))"></p-tag>
                    @if (canCancelRelease(groupRelease(group))) {
                      <p-button label="Cancel" icon="pi pi-ban" severity="danger" [text]="true" size="small"
                        (onClick)="$event.stopPropagation(); openCancelDialog(groupRelease(group))"></p-button>
                    }
                    @if (group.children.length) {
                      <p-tag [value]="groupChildCountLabel(group)" severity="info"></p-tag>
                    }
                    @if (group.integrityMessage) {
                      <p-tag [value]="group.integrityMessage" severity="danger"></p-tag>
                    }
                    @if (!isPlanable(groupRelease(group))) {
                      <p-tag [value]="planableReason(groupRelease(group))" severity="secondary"></p-tag>
                    }
                    <div class="need-by-badge" [ngClass]="'need-by-badge--' + needByState(groupRelease(group))">
                      <span>Need by</span>
                      <strong>{{ needByLabel(groupRelease(group)) }}</strong>
                    </div>
                  </div>
                  <div class="group-context">
                    @if (groupRelease(group).crossDockOrchestrationId) {
                      <span>{{ orchestrationLabel(groupRelease(group)) }}</span>
                    }
                    @if (logisticsProjectLabel(groupRelease(group))) {
                      <span>Project {{ logisticsProjectLabel(groupRelease(group)) }}</span>
                    }
                  </div>
                  <div class="group-overview">
                    <div class="group-overview-cell">
                      <span>Pickup</span>
                      <strong>{{ collapsedPickupLabel(groupRelease(group)) }}</strong>
                      <small>{{ collapsedPickupMeta(groupRelease(group)) }}</small>
                    </div>
                    <div class="group-overview-cell">
                      <span>Dropoff</span>
                      <strong>{{ collapsedDropoffLabel(groupRelease(group)) }}</strong>
                      <small>{{ collapsedDropoffMeta(groupRelease(group)) }}</small>
                    </div>
                    <div class="group-overview-cell">
                      <span>Item / load</span>
                      @if (releaseLines(groupRelease(group))[0]; as line) {
                        <strong>{{ itemName(line) }}</strong>
                        <small>{{ line.itemNumber || line.itemCode || 'Item number not set' }} @if (line.skuNumber || line.inventoryStockUnitCode) { · {{ lineSkuLabel(line) }} }</small>
                        <small>{{ lineQuantity(line) }} · {{ itemSizeLabel(line) }}</small>
                        @if (itemVolumeLabel(line)) { <small>{{ itemVolumeLabel(line) }}</small> }
                        @if (releaseLines(groupRelease(group)).length > 1) { <small>+{{ releaseLines(groupRelease(group)).length - 1 }} more item lines</small> }
                      } @else { <strong>No item lines</strong> }
                    </div>
                  </div>
                </div>
              </div>
              <div class="group-summary">
                <div class="group-summary-copy">
                  <div class="group-summary-kicker">{{ group.children.length ? 'Leg Progress' : 'Release Progress' }}</div>
                  <div class="group-summary-value">{{ groupProgressLabel(group) }}</div>
                </div>
                <div class="group-summary-status">
                  <p-tag [value]="planningStateLabel(groupRelease(group))" [severity]="planningSeverity(groupRelease(group))"></p-tag>
                </div>
              </div>
            </div>

            @if (isGroupExpanded(group.key)) {
              <div class="group-body">
                <ng-container *ngTemplateOutlet="releaseDetails; context: { $implicit: groupRelease(group) }"></ng-container>

                @if (group.children.length) {
                  <div class="child-section">
                    <div class="child-section-header">
                      <div>
                        <div class="child-section-title">Orchestration Legs</div>
                        <div class="child-section-copy">Child releases generated from the original order release for cross-dock execution.</div>
                      </div>
                      <div class="child-section-summary">{{ groupProgressLabel(group) }}</div>
                    </div>

                    <div class="child-list">
                      @for (leg of group.children; track leg.releaseId || leg.releaseNumber || $index) {
                        <div class="child-row">
                          <div class="child-row-header">
                            <div class="child-row-heading">
                              <label class="group-select child-select">
                                <p-checkbox
                                  [binary]="true"
                                  [disabled]="!isCurrentFilterMatch(leg) || !isPlanable(leg) || (selectAllMatching && isRescheduled(leg))"
                                  [ngModel]="isSelected(leg)"
                                  (ngModelChange)="toggleSelection(leg, $event)"
                                  [ngModelOptions]="{ standalone: true }"></p-checkbox>
                              </label>
                              <div>
                                <div class="child-row-title">
                                  <a class="release-link" [routerLink]="['/transport-planning/releases', leg.releaseId]">
                                    {{ leg.releaseNumber || ('Release #' + (leg.releaseId || '-')) }}
                                  </a>
                                  <p-tag [value]="legLabel(leg)" severity="info"></p-tag>
                                  @if (leg.crossDockLegType) {
                                    <p-tag [value]="leg.crossDockLegType" severity="secondary"></p-tag>
                                  }
                                  @if (!isPlanable(leg)) {
                                    <p-tag [value]="planableReason(leg)" severity="secondary"></p-tag>
                                  }
                                  <div class="need-by-badge need-by-badge--compact" [ngClass]="'need-by-badge--' + needByState(leg)">
                                    <span>Need by</span>
                                    <strong>{{ needByLabel(leg) }}</strong>
                                  </div>
                                </div>
                                <div class="child-row-meta">
                                  <span>{{ sourceReferenceLabel(leg) }}</span>
                                  @if (leg.customerTrackingNumber) {
                                    <span>Tracking {{ leg.customerTrackingNumber }}</span>
                                  }
                                  @if (hasInboundTransferGate(group, leg) && (leg.releaseStatus === 'READY' || leg.releaseStatus === 'RELEASED_TO_SHIPMENT')) {
                                    <span>Planning is open; warehouse receipt is still checked before delivery execution.</span>
                                  }
                                </div>
                              </div>
                            </div>
                            <div class="child-row-status">
                              <p-tag [value]="planningStateLabel(leg)" [severity]="planningSeverity(leg)"></p-tag>
                            </div>
                          </div>

                          <div class="child-row-grid">
                            <div class="detail-card compact">
                              <span>Customer</span>
                              <strong>{{ customerLabel(leg) }}</strong>
                              <small>{{ accountLabel(leg) || customerMeta(leg) || 'No CRM client or legacy customer linked' }}</small>
                            </div>
                            <div class="detail-card compact">
                              <span>Route</span>
                              <strong>{{ leg.originName || 'Start place missing' }}</strong>
                              <small>to {{ leg.destinationName || 'End place missing' }}</small>
                            </div>
                            <div class="detail-card compact">
                              <span>Load</span>
                              <strong>{{ loadSummary(leg) }}</strong>
                              <small>{{ releaseLines(leg).length }} item line{{ releaseLines(leg).length === 1 ? '' : 's' }}</small>
                            </div>
                            <div class="detail-card compact">
                              <span>Planning</span>
                              <strong>{{ linkedShipmentLabel(leg) }}</strong>
                              <small>{{ assignmentSummary(leg) || 'Assignment pending' }}</small>
                            </div>
                            <div class="detail-card compact">
                              <span>Window</span>
                              <strong>{{ releaseWindowLabel(leg) }}</strong>
                              <small>{{ deliveryWindowLabel(leg) }}</small>
                            </div>
                            <div class="detail-card compact">
                              <span>Execution Note</span>
                              <strong>{{ releaseExecutionNote(leg) }}</strong>
                              <small>{{ routeSummary(leg) }}</small>
                            </div>
                          </div>
                        </div>
                      }
                    </div>
                  </div>
                }
              </div>
            }
          </section>
        }
      </div>
    }

    @if (paginationTotalRecords() > tableRows) {
      <div class="release-pagination">
        <p-paginator
          [rows]="tableRows"
          [first]="first"
          [totalRecords]="paginationTotalRecords()"
          [rowsPerPageOptions]="rowsPerPageOptions"
          [showFirstLastIcon]="true"
          [showCurrentPageReport]="true"
          [currentPageReportTemplate]="'Showing {first}-{last} of {totalRecords} ' + paginationEntryLabel()"
          (onPageChange)="onPageChange($event)"></p-paginator>
      </div>
    }
  </app-page-data-panel>
</app-page-shell>
<p-dialog header="Prepare rescheduled cargo" [visible]="!!rescheduleTarget"
  (visibleChange)="!$event && (rescheduleTarget = undefined)" [modal]="true"
  [style]="{ width: 'min(780px, 95vw)' }" [breakpoints]="{ '680px': '96vw' }"
  styleClass="release-reschedule-dialog" [draggable]="false" [resizable]="false"
  [closable]="!isPreparingReschedule">
  <div class="release-reschedule-body">
    <aside class="release-reschedule-summary">
      <span class="release-reschedule-icon"><i class="pi pi-calendar-clock"></i></span>
      <div>
        <small>RESCHEDULED CARGO</small>
        <h3>{{ rescheduleTarget?.releaseNumber }}</h3>
        <p>Prepare a new release for the rescheduled quantity. The original shipment and its delivery history stay linked to this release.</p>
      </div>
      <div class="release-reschedule-route">
        <span>Pickup <strong>{{ rescheduleTarget?.originName || 'Location missing' }}</strong></span>
        <span>Dropoff <strong>{{ rescheduleTarget?.destinationName || 'Location missing' }}</strong></span>
      </div>
    </aside>
    <section class="release-reschedule-content">
      <h4>New delivery commitment</h4>
      <p>Choose when the rescheduled cargo is needed. The return stop must be complete and warehouse stock must be available.</p>
      <p-inputGroup>
        <p-inputGroupAddon><i class="pi pi-calendar"></i></p-inputGroupAddon>
        <p-iftalabel>
          <p-datepicker inputId="reschedule-need-by" [(ngModel)]="rescheduleNeedBy"
            [showTime]="true" hourFormat="24" [showIcon]="true" appendTo="body"
            styleClass="w-full"></p-datepicker>
          <label for="reschedule-need-by">New need-by date and time *</label>
        </p-iftalabel>
      </p-inputGroup>
      <p-button label="Use current time" icon="pi pi-clock" severity="secondary" [text]="true"
        [disabled]="isPreparingReschedule" (onClick)="useCurrentRescheduleTime()"></p-button>
      @if (rescheduleError) { <div class="notice" role="alert">{{ rescheduleError }}</div> }
    </section>
  </div>
  <ng-template pTemplate="footer">
    <p-button label="Back" severity="secondary" [outlined]="true"
      [disabled]="isPreparingReschedule" (onClick)="rescheduleTarget = undefined"></p-button>
    <p-button label="Prepare and Select" icon="pi pi-check" [loading]="isPreparingReschedule"
      [disabled]="!rescheduleNeedBy || isPreparingReschedule" (onClick)="prepareRescheduledRelease()"></p-button>
  </ng-template>
</p-dialog>
<p-dialog header="Cancel order release family" [(visible)]="cancelDialogVisible" [modal]="true"
  [style]="{ width: '36rem', maxWidth: '95vw' }" [draggable]="false">
  <p>Cancel {{ cancelTarget?.releaseNumber || 'this release' }} and its linked work?</p>
  @if (cancellationImpact; as impact) {
    <p>{{ impact.releaseIds.length }} release(s), {{ impact.transferIds.length }} warehouse transfer(s), and {{ impact.dispatchIds.length }} warehouse dispatch(es) will be cancelled.</p>
    @if (impact.releaseIds.length > 1) { <p>Child releases: {{ impact.releaseIds.slice(1).join(', ') }}</p> }
    @if (impact.transferIds.length) { <p>Transfers: {{ impact.transferIds.join(', ') }}</p> }
    @if (impact.dispatchIds.length) { <p>Dispatches: {{ impact.dispatchIds.join(', ') }}</p> }
  } @else { <p>Loading linked work...</p> }
  <label for="list-release-cancellation-reason">Reason *</label>
  <textarea id="list-release-cancellation-reason" [(ngModel)]="cancellationReason" rows="4" maxlength="500" class="w-full"></textarea>
  @if (cancelDialogError) { <div class="notice">{{ cancelDialogError }}</div> }
  <ng-template pTemplate="footer">
    <p-button label="Keep Release" severity="secondary" [outlined]="true" (onClick)="cancelDialogVisible = false"></p-button>
    <p-button label="Cancel Release Family" severity="danger" icon="pi pi-ban" [loading]="isCancelling"
      [disabled]="!cancellationImpact || !cancellationReason.trim()" (onClick)="cancelRelease()"></p-button>
  </ng-template>
</p-dialog>
